// src/api/selection_modifiers.ts

import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';

// Define la URL base para los selection_modifiers
const BASE_URL = NAMESPACES.menu_api.selection_modifiers;

// Función auxiliar para configurar la instancia de Axios
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'menu_api',
  group: 'selection_modifiers',
  defaultTtl: '5m',
  hashLargeSegments: true,
  emitMeta: true,
});

/**
 * Obtiene todos los modificadores por ID local.
 * @param local_id - El ID local asociado a los modificadores.
 * @returns Una promesa con la respuesta de la API.
 */
export const GetAllModifiersByLocalId = (local_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetAllModifiersByLocalId/${local_id}`)
  );
};

/**
 * Obtiene todos los modificadores asociados a un ítem específico.
 * @param item_id - El ID del ítem.
 * @returns Una promesa con la respuesta de la API.
 */
export const GetModifiersByItemId = (item_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetModifiersByItemId/${item_id}`)
  );
};

// /GetAllModifierItemsByModifierId/<int:modifier_id>

/**
 * Obtiene todos los ítems asociados a un modificador específico.
 * @param modifier_id - El ID del modificador.
 * @returns Una promesa con la respuesta de la API.
 */

export const GetAllModifierItemsByModifierId = (modifier_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetAllModifierItemsByModifierId/${modifier_id}`)
  );
}

/**
 * Crea un nuevo modificador.
 * @param data - Objeto que contiene los datos necesarios para crear el modificador.
 * @returns Una promesa con la respuesta de la API.
 */
export const CreateSelectionModifier = async (data: {
  modifier_name: string;
  modifier_description?: string;
  local_id: number;
}) => {
  const res = await retryWithBackoff(() =>
    api.post('/CreateSelectionModifier', data)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Obtiene un modificador por su ID.
 * @param modifier_id - El ID del modificador.
 * @returns Una promesa con la respuesta de la API.
 */
export const GetSelectionModifierById = (modifier_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetSelectionModifierById/${modifier_id}`)
  );
};

/**
 * Elimina un modificador por su ID.
 * @param modifier_id - El ID del modificador a eliminar.
 * @returns Una promesa con la respuesta de la API.
 */
export const DeleteSelectionModifier = async (modifier_id: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteSelectionModifier/${modifier_id}`)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Asocia un modificador con un ítem.
 * @param data - Objeto que contiene los datos necesarios para asociar el modificador con el ítem.
 * @returns Una promesa con la respuesta de la API.
 */
export const AddModifierToItem = async (data: {
  item_id: number;
  modifier_id: number;
  is_optional?: boolean;
  is_price_setter?: boolean;
}) => {
  const res = await retryWithBackoff(() =>
    api.post('/AddModifierToItem', data)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Desasocia un modificador de un ítem.
 * @param item_id - El ID del ítem.
 * @param modifier_id - El ID del modificador.
 * @returns Una promesa con la respuesta de la API.
 */
export const RemoveModifierFromItem = async (item_id: number, modifier_id: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/RemoveModifierFromItem/${item_id}/${modifier_id}`)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Obtiene todas las opciones de un modificador por su ID.
 * @param modifier_id - El ID del modificador.
 * @returns Una promesa con la respuesta de la API.
 */
export const GetModifierOptionsByModifierId = (modifier_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetModifierOptionsByModifierId/${modifier_id}`)
  );
};

/**
 * Agrega una opción a un modificador.
 * @param data - Objeto que contiene los datos necesarios para agregar la opción al modificador.
 * @returns Una promesa con la respuesta de la API.
 */
export const AddOptionToModifier = async (data: {
  modifier_id: number;
  option_name: string;
  option_description?: string;
  price_modifier?: number;
}) => {
  const res = await retryWithBackoff(() =>
    api.post('/AddOptionToModifier', data)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Elimina una opción de un modificador.
 * @param modifier_id - El ID del modificador.
 * @param option_id - El ID de la opción a eliminar.
 * @returns Una promesa con la respuesta de la API.
 */
export const RemoveOptionFromModifier = async (modifier_id: number, option_id: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/RemoveOptionFromModifier/${modifier_id}/${option_id}`)
  );
  await cachedApi.clear();
  return res;
};

// /UpdateSelectionModifierNameById

/**
 * Actualiza el nombre de un modificador por su ID.
 * @param data - Objeto que contiene los datos necesarios para actualizar el nombre del modificador.
 * @param modifier_id - El ID del modificador.
 * @returns Una promesa con la respuesta de la API.
 */

export const UpdateSelectionModifierNameById = async (data: {
  modifier_name: string;
}, modifier_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateSelectionModifierNameById/${modifier_id}`, data)
  );
  await cachedApi.clear();
  return res;
}

 ///UpdateSelectionModifierDescriptionById/<int:modifier_id>

/**
 * Actualiza la descripción de un modificador por su ID.
 * @param data - Objeto que contiene los datos necesarios para actualizar la descripción del modificador.
 * @param modifier_id - El ID del modificador.
 * @returns Una promesa con la respuesta de la API.
 * 
 */
export const UpdateSelectionModifierDescriptionById = async (data: {
  modifier_description: string;
}, modifier_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateSelectionModifierDescriptionById/${modifier_id}`, data)
  );
  await cachedApi.clear();
  return res;
}

// /UpdateModifierOptionNameByOptionId/<int:option_id>

/**
 * Actualiza el nombre de una opción de un modificador por su ID.
 * @param data - Objeto que contiene los datos necesarios para actualizar el nombre de la opción.
 * @param option_id - El ID de la opción.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateModifierOptionNameByOptionId = async (data: {
  option_name: string;
}, option_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateModifierOptionNameByOptionId/${option_id}`, data)
  );
  await cachedApi.clear();
  return res;
}

// /UpdateModifierOptionDescriptionByOptionId/<int:option_id>

/**
 * Actualiza la descripción de una opción de un modificador por su ID.
 * @param data - Objeto que contiene los datos necesarios para actualizar la descripción de la opción.
 * @param option_id - El ID de la opción.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateModifierOptionDescriptionByOptionId = async (data: {
  option_description: string;
}, option_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateModifierOptionDescriptionByOptionId/${option_id}`, data)
  );
  await cachedApi.clear();
  return res;
}

// /UpdateModifierOptionPriceModifierByOptionId/<int:option_id>

/**
 * Actualiza el precio de una opción de un modificador por su ID.
 * @param data - Objeto que contiene los datos necesarios para actualizar el precio de la opción.
 * @param option_id - El ID de la opción.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateModifierOptionPriceModifierByOptionId = async (price_modifier: number, option_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateModifierOptionPriceModifierByOptionId/${option_id}`, {
      price_modifier: price_modifier
    })
  );
  await cachedApi.clear();
  return res;
}

// /ChangeIsOptionalStatusOfItemModifier/<int:item_id>/<int:modifier_id>

/**
 * Cambia el estado de opcionalidad de un modificador asociado a un ítem.
 * @param item_id - El ID del ítem.
 * @param modifier_id - El ID del modificador.
 * @returns Una promesa con la respuesta de la API.
 */

export const ChangeIsOptionalStatusOfItemModifier = async (item_id: number, modifier_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/ChangeIsOptionalStatusOfItemModifier/${item_id}/${modifier_id}`)
  );
  await cachedApi.clear();
  return res;
}

// /ChangeIsPriceSetterStatusOfItemModifier/<int:item_id>/<int:modifier_id>

/**
 * Cambia el estado de fijador de precio de un modificador asociado a un ítem.
 * @param item_id - El ID del ítem.
 * @param modifier_id - El ID del modificador.
 * @returns Una promesa con la respuesta de la API.
 */

export const ChangeIsPriceSetterStatusOfItemModifier = async (item_id: number, modifier_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/ChangeIsPriceSetterStatusOfItemModifier/${item_id}/${modifier_id}`)
  );
  await cachedApi.clear();
  return res;
}

/**
 * Get modifiers configured for a specific item with detailed structure including all configuration.
 * @param item_id - The item identifier.
 * @returns A promise with the API response.
 */
export const GetModifiersForItemById = (item_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetModifiersForItemById/${item_id}`)
  );
};